import { useParams } from 'react-router-dom';
import { useMemo } from 'react';
import { omit } from 'lodash';
import { toast } from 'sonner';

import SegmentDetailsForm from '@/components/Pages/Segments/CreateSegment/SegmentDetailsForm';
import PageHeader from 'components/Common/PageHeader';
import LayoutGap from '@/components/Common/LayoutGap';
import { useEditSegment, useSegment } from '@/refactored/hooks/useSegment';
import type { SegmentDetailsFormValues } from '@/components/Pages/Segments/schema';
import { tryCatch } from '@/utils/tryCatch';
import Spinner, {
  Color as SpinnerColor,
  Size as SpinnerSize,
} from '@/components/Common/Spinner';
import { Alert } from 'flowbite-react';

const EditSegmentPage = () => {
  // const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [{ data: segment, loading: segmentLoading, error: segmentError }] =
    useSegment(id!);
  const [{ loading: isSavingSegment, error }, editSegment] = useEditSegment(
    id!,
  );

  const handleSubmit = async (data: SegmentDetailsFormValues) => {
    const segmentDetails = omit(data, [
      'size',
      'created',
      'entity',
      'modified',
      ...(segment?.status !== 'DRAFT' ? ['rules', 'segmentType'] : []),
    ]);

    const newData = {
      ...segmentDetails,
      rules: segmentDetails.rules?.map((rule) => {
        const newRule = omit(rule, [
          'id',
          'entity',
          'modified',
          'created',
          'segmentId',
        ]);

        if (newRule.ruleType === 'purchases') {
          const { dateRange, ...rest } = newRule;
          return {
            ...rest,
            startDate: dateRange.from,
            endDate: dateRange.to,
          };
        }

        return newRule;
      }),
    };

    const [err] = await tryCatch(editSegment({ data: newData }));

    if (err) {
      toast.error('Error updating segment');
      return;
    }

    toast.success('Segment updated successfully');
  };

  const currentSegment = useMemo<SegmentDetailsFormValues | undefined>(
    () =>
      segment
        ? {
            ...segment,
            rules: segment.rules.map((rule) => {
              if (rule.ruleType === 'purchases') {
                return {
                  ...rule,
                  dateRange: {
                    from: rule.startDate,
                    to: rule.endDate,
                  },
                };
              }

              return rule;
            }),
          }
        : undefined,
    [segment],
  );

  return (
    <LayoutGap direction="column">
      <PageHeader title="Edit Segment" className="mx-6 mt-4" />
      <div className="mx-6">
        {segmentLoading && (
          <Spinner
            size={SpinnerSize.L}
            color={SpinnerColor.Black}
            className="self-center justify-self-center"
          />
        )}
        {!segmentLoading && segment && (
          <SegmentDetailsForm
            onSubmit={handleSubmit}
            isSubmitting={isSavingSegment}
            submitError={error}
            defaultValues={currentSegment}
            isEditing
          />
        )}
        {segmentError && (
          <div>
            <Alert color="failure">Error loading segment</Alert>
          </div>
        )}
      </div>
    </LayoutGap>
  );
};

export default EditSegmentPage;
